import {
  Component,
  ChangeDetectionStrategy,
  EventEmitter,
  Input,
  Output,
  OnInit,
  OnDestroy
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/concatMap';

import {
  Utils,
  Translate,
  SocketHandler,
  PageEventsHandler
} from '../../services';
import {
  ViewActions,
  ViewService,
  AssessmentActions,
  AssessmentService
} from '../../logics';
import {
  REQUEST_FULLSCREEN,
  FREE_INSERT_MODE,
  TRAINING_MODE,
  USE_WATERMARK,
  FEEDBACK_URL,
  SHOW_TITLE_IN_UNDERSTANDING_TEXT,
  NOTIFY_CONNECTION_STATUS_WITH_GROWL,
  WELCOME_PAGE,
  ANSWER_SUBST_TEMPLATE_0,
  ANSWER_SUBST_TEMPLATE_1
} from '../../constants';

@Component({
  selector: 'survey-layout',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
        *ngIf="!(newSectionError$ | async) && !identifyByFailed"
        class="cs-main"
        [ngClass]="{
        'cs-no-timer': isCompleted || freeInsert
      }">
      <cs-user-bar
          [inputs]="{
          user: shared.userData,
          sectionColor: (currentSection$ | async)?.colore,
          trainingMode: trainingMode,
          feedbackUrl: feedbackUrl,
          showHelp: true,
          testMetadata: (assessmentMeta$ | async)
        }"
          (highContrastToggle)="highContrastToggle.emit()"
          (increaseZoom)="increaseZoomHandler()"
          (decreaseZoom)="decreaseZoomHandler()"
          (expandImage)="expandImage.emit($event)"
          (openHelp)="openHelp.emit()">
      </cs-user-bar>
      <cs-main-bar
          [inputs]="{
          sections: (ricetteNotHide$ | async), 
          loadedRicette: (loadedRicette$ | async), 
          progress: (sectionsProgress$ | async),
          current: (currentSection$ | async),
          isCompleted: freeInsert || (completed$ | async),
          canChangeSection: !(assessmentMeta$ | async).cambioSezPrefissato
        }"
          (requestNewSection)="onRequestNewSection($event)">
      </cs-main-bar>
      
      <cs-navigator
          (terminate)="onTerminate($event)"
          (requestNewQuestion)="onRequestNewQuestion($event)"
          [inputs]="{
          questions: (questionsData$ | async), 
          progress: (questionsProgress$ | async),
          current: (currentQuestion$ | async),
          sectionColor: (currentSection$ | async)?.colore,
          isCompleted: (completed$ | async),
          isLastSection: (isLastSection$ | async),
          showTerminateButton: (completed$ | async) || !(assessmentMeta$ | async).cambioSezPrefissato
        }">
      </cs-navigator>

      <cs-timer
          *ngIf="!(completed$ | async) && !freeInsert"
          [inputs]="shared.timerData">
      </cs-timer>

      <cs-page-title
          [inputs]="{
          title: (currentSection$ | async)?.nome +
            ' > ' + questionTitle,
          currentPlayerId: (currentPlayerId$ | async),
          currentQuestion: (currentQuestion$ | async),
          refreshEnabled: (currentQuestionMap$ | async)?.refresh_enabled,
          titleOverridePrefix: (currentSection$ | async)?.nome + ' > ',
          titleOverride: (titleOverride$ | async)
        }"
          (sendLog)="onSendLog($event)"
          (play)="onPlay($event)"
          (stop)="onStop($event)">
      </cs-page-title>
      <cs-page-content
          [ngStyle]="{
            'zoom': zoom + '%',
            '-ms-zoom': zoom + '%'
          }"
          (setAnswer)="onSetAnswer($event)"
          (play)="onPlay($event)"
          (stop)="onStop($event)"
          [ricettaId]="(ricettaId$ | async)"
          [inputs]="{
          image: (currentImg$ | async),
          currentSection: (currentSection$ | async),
          currentQuestion: (currentQuestion$ | async),
          currentAnswer: (currentAnswer$ | async),
          currentUnderstandingText: (understandingText$ | async),
          currentPlayerId: (currentPlayerId$ | async),
          watermarkImage: (imageWatermark$ | async),
          polygons: (currentPolygons$ | async),
          sectionColor: (currentSection$ | async)?.colore,
          isCompleted: (completed$ | async),
          confirmToPlayAudio: true
        }">
      </cs-page-content>
    </div>

    <div class="cs-simple-page" *ngIf="(newSectionError$ | async) || identifyByFailed">
      <div class="cs-simple-page__content">
        <p class="cs-simple-page__text">
          {{ (translations$ | async)['wait#test_section_error']}}
        </p>
        <button
            class="cs-btn cs-btn-cta cs-btn-xl cs-btn-start-test"
            (click)="refreshPageClick()">
          {{ (translations$ | async)['wait#back_to_login']}}
        </button>
      </div>
    </div>
  `
})

export class SurveyComponent implements OnInit, OnDestroy {
  currentImage;
  currents$: Observable<any>;
  currentSection$: Observable<any>;
  currentQuestion$: Observable<any>;
  currentAnswer$: Observable<any>;
  currentImg$: Observable<any>;
  ricettaId$: Observable<string>;
  currentPolygons$: Observable<any>;
  understandingText$: Observable<any>;
  imageWatermark$: Observable<any>;

  assessmentMeta$: Observable<any>;

  sectionsProgress$: Observable<any>;
  questionsProgress$: Observable<any>;

  ricette$: Observable<any>;
  ricetteNotHide$: Observable<any>;
  questionsData$: Observable<any>;

  completed$: Observable<any>;
  isLastSection$: Observable<any>;

  currentQuestionTime$: Observable<any>;
  sectionTimeTerminated$: Observable<any>;
  translations$: Observable<any>;
  currentQuestionMap$: Observable<any>;
  loadedRicette$: Observable<any>;
  lastSectionTimeUpdateStatus$: Observable<any>;
  lastSetAnswerStatus$: Observable<any>;

  sectionsData: Array<any>;
  questionData: Array<any>;
  questionTitle: string = '';
  isCompleted: boolean = false;
  isLastSection: boolean = false;
  freeInsert: boolean = FREE_INSERT_MODE;
  showTitleInUnderstandingText: boolean = SHOW_TITLE_IN_UNDERSTANDING_TEXT;
  trainingMode: boolean = TRAINING_MODE;
  feedbackUrl: string = FEEDBACK_URL;
  translations: Object = {};
  requestFullscreen: boolean;

  countMap$: Subject<Object> = new Subject<Object>();
  titleOverride$: Subject<Object> = new Subject<Object>();
  currentUnderstandingText$: Subject<any> = new Subject<any>();
  newSectionbyId$: Subject<any> = new Subject<any>();
  currentPlayerId$: Subject<any> = new Subject<any>();
  destroyed$: Subject<any> = new Subject<any>();
  newSectionError$: Observable<boolean>;
  identifyByFailed: boolean = false;

  zoom = 100;

  @Input() shared: Object = {};
  @Output() viewChange = new EventEmitter<string>();
  @Output() openHelp = new EventEmitter<Object>();
  @Output() highContrastToggle = new EventEmitter<Object>();
  @Output() increaseZoom = new EventEmitter<Object>();
  @Output() decreaseZoom = new EventEmitter<Object>();
  @Output() expandImage = new EventEmitter<Object>();

  constructor (private store: Store<any>,
               private utils: Utils,
               private translate: Translate,
               private socketHandler: SocketHandler,
               private viewActions: ViewActions,
               private viewService: ViewService,
               private pageEventsHandler: PageEventsHandler,
               private assessmentActions: AssessmentActions,
               private assessmentService: AssessmentService) {

    this.requestFullscreen = REQUEST_FULLSCREEN && this.utils.isWeb();
    this.translations$ = this.translate.getTranslations$();

    this.ricette$ = this.assessmentService.getRicetteMetadata$();
    this.currentSection$ = this.assessmentService.getCurrentSectionMetadata$();
    this.assessmentMeta$ = this.assessmentService.getMetadata$();
    this.currentQuestion$ = this.assessmentService.getCurrentQuestion$();
    this.sectionsProgress$ = this.assessmentService.getSectionsProgress$();
    this.questionsProgress$ = this.assessmentService.getQuestionsProgress$();
    this.completed$ = this.assessmentService.isCompleted$();
    this.isLastSection$ = this.assessmentService.isCurrentTheLastSection$();
    this.loadedRicette$ = this.assessmentService.getLoadedRicette$();
    this.lastSectionTimeUpdateStatus$ = this.assessmentService.getLastSectionTimeUpdateStatus$();
    this.lastSetAnswerStatus$ = this.assessmentService.getLastSetAnswerStatus$();

    this.currentQuestionMap$ = this.assessmentService.getCurrentQuestion$()
      .switchMap(({id}) => this.assessmentService.getQuestionMapById$(id));

    this.imageWatermark$ = this.assessmentService
      .getMetadata$()
      .filter(() => USE_WATERMARK)
      .map(metadata => metadata.watermarkImage);

    this.ricetteNotHide$ = this.ricette$
      .withLatestFrom(this.assessmentService.getRicetteDetails$())
      .map(([ricette, details]) => Object.keys(ricette)
        .reduce((res, k) => {
          if (typeof details[ricette[k].id] === 'undefined' || !details[ricette[k].id].hide) {
            res.push(ricette[k]);
          }
          return res;
        }, []));

    this.currentQuestionTime$ = this.currentQuestion$
      .takeUntil(this.completed$.filter(complete => complete))
      .withLatestFrom(this.questionsProgress$)
      .map(([question, progress]) => {
        let viewTimeProgress = 0
          , viewTimeVolatile = this.assessmentService.getQuestionViewTime(question.id);
        viewTimeVolatile = parseInt(viewTimeVolatile);

        if (typeof progress[question.id] !== 'undefined') {
          viewTimeProgress = parseInt(progress[question.id].viewTime || 0);
        }

        return {
          question: question,
          currentTime: Math.max(viewTimeProgress, viewTimeVolatile)
        };
      })
      .switchMap(({question, currentTime}) => this.utils
        .resettableTimer$(this.currentQuestion$.mapTo(true), null, currentTime)
        .map(tick => ({question: question, currentTime: currentTime + tick})));

    this.currentAnswer$ = Observable.combineLatest(
      this.currentQuestion$,
      this.questionsProgress$)
      .map(([{id}, progress]) => progress[id]);

    this.questionsData$ = this.currentSection$
      .switchMap(section => this.assessmentService.getRicettaById$(section.id))
      .withLatestFrom(Observable.combineLatest(
        this.questionsProgress$,
        this.completed$))
      .map(([ricetta, [progress, isCompleted]]) => {
        let titleOverride = {}
          , titleOverrideComponents
          , titleOverrideItem
          , countMap = {}
          , idx = 0
          , questionData = []
          , understandingText
          , navigatorItem
        ;

        let currentTurno, currentDomande, currentDomanda;

        let isCompletedByQuestionId = (questionId) => {
          return progress &&
            progress[questionId] &&
            typeof progress[questionId].answerId !== 'undefined' &&
            progress[questionId].answerId !== -1;
        };

        for (let i = 0; i < ricetta.turni.length; i++) {
          currentTurno = ricetta.turni[i];
          currentDomande = currentTurno.domande;
          understandingText = undefined;

          if (typeof currentTurno.testoRiferimento !== 'undefined') {
            if (typeof currentTurno.testoRiferimento.text === 'undefined') {
              questionData.push({
                label: 'T',
                id: currentTurno.testoRiferimento.id,
                comprensionText: true
              });

              countMap[currentTurno.testoRiferimento.id] = this
                .translations['survey#testo_comprensione'];
            } else {
              understandingText = this.showTitleInUnderstandingText
                ? currentTurno.testoRiferimento.title + '<br/>'
                : '';
              understandingText += currentTurno.testoRiferimento.text;
            }
          }

          if (typeof currentTurno.layout !== 'undefined' && currentTurno.layout === 'select' || currentTurno.layout === 'dropdown_choices') {
            navigatorItem = {
              label: 'T<i class="iconma ion-arrow-down-b"></i>',
              multipleAnswer: true,
              understandingText: understandingText,
              otherQuestionIds: [],
              id: null,
              meta: null
            };
            titleOverrideComponents = [
              this.translations['survey#domande'],
              '',
              '-',
              ''
            ];
            titleOverrideItem = {
              title: ''
            };
            for (let j = 0; j < currentDomande.length; j++) {
              idx++;
              countMap[currentDomande[j].id] = this
                .translations['survey#domanda'] + ' ' + idx;

              titleOverride[currentDomande[j].id] = titleOverrideItem;

              if (isCompleted) {
                if (isCompletedByQuestionId(currentDomande[j].id)) {
                  countMap[currentDomande[j].id] = countMap[currentDomande[j].id] + ' ' + this
                    .translations['survey#domanda_wrong'];
                } else {
                  countMap[currentDomande[j].id] = countMap[currentDomande[j].id] + ' ' + this
                    .translations['survey#domanda_empty'];
                }
              }
              if (!currentDomande[j].navigatorShow) {
                navigatorItem.otherQuestionIds.push(currentDomande[j].id);
                continue;
              }
              else {
                titleOverrideComponents[1] = idx;
              }
              navigatorItem.id = currentDomande[j].id;
              navigatorItem.meta = currentDomande[j].meta;
            }

            titleOverrideComponents[3] = idx;
            titleOverrideItem.title = titleOverrideComponents.join(' ');

            questionData.push(navigatorItem);
          }
          else {
            for (let j = 0; j < currentDomande.length; j++) {
              currentDomanda = currentDomande[j];
              if (!currentDomanda.hide) {
                questionData.push({
                  label: String(++idx),
                  id: currentDomanda.id,
                  meta: currentDomanda.meta,
                  understandingText: understandingText
                });

                countMap[currentDomanda.id] = this
                  .translations['survey#domanda'] + ' ' + idx;

                if (isCompleted) {
                  if (isCompletedByQuestionId(currentDomanda.id)) {
                    countMap[currentDomanda.id] = countMap[currentDomanda.id] + ' ' + this
                      .translations['survey#domanda_wrong'];
                  } else {
                    countMap[currentDomanda.id] = countMap[currentDomanda.id] + ' ' + this
                      .translations['survey#domanda_empty'];
                  }
                }
              } else {
                ++idx;
              }
            }
          }
        }

        this.countMap$.next(countMap);
        this.titleOverride$.next(titleOverride);

        return questionData;
      });

    this.currentImg$ = this.currentQuestion$
      .withLatestFrom(this.assessmentService.getQuestionsMap$())
      .map(([{id, hash}, questionMap]) => {
        let ricettaImages = this.assessmentService
          .getImagesByRicettaId(questionMap[id].ricetta) || {};
        return ricettaImages[hash];
      });

    this.ricettaId$ = this.currentQuestion$
      .withLatestFrom(this.assessmentService.getQuestionsMap$())
      .map(([{id, hash}, questionMap]) => {
        return questionMap[id].ricetta;
      });

    this.currentPolygons$ = this.currentQuestion$
      .map(currentQuestion => {
        let polygons = [];

        for (let i in currentQuestion.meta) {
          if (i.indexOf('text') === -1) {
            polygons.push(currentQuestion.meta[i]);
          }
        }

        return polygons;
      });

    this.understandingText$ = Observable.combineLatest(
      this.questionsProgress$,
      this.currentSection$,
      this.currentQuestion$)
      .withLatestFrom(Observable.combineLatest(this.currentSection$
        .switchMap(section => this.assessmentService.getRicettaById$(section.id)),
        this.questionsData$,
        this.currentSection$))
      .takeUntil(this.destroyed$)
      //.debounceTime(1)
      .map(([[progress, currentSection, {id}], [{turni}, questionsData, {colore}]]) => {
        // if (typeof text === 'undefined' || !turni) {
        //   return;
        // }

        let j
          , i
          , currentTurno
          , selectedIdx
          , answerText
          //, answerTitle
          , placeholderType = 0
          , placeholderMaps = [
            {template: ANSWER_SUBST_TEMPLATE_0},
            {template: ANSWER_SUBST_TEMPLATE_1}
          ]
        ;

        let strip = (html) => {
          let tmp = document.createElement('DIV');
          tmp.innerHTML = html;
          return tmp.textContent || tmp.innerText || '';
        };

        // Scelta turno in base all'id domanda.
        for (i = 0; i < turni.length; i ++) {
          for (j = 0; j < turni[i].domande.length; j++) {
            if (turni[i].domande[j].id == id) {
              currentTurno = turni[i];
              break;
            }
          }
          if (typeof currentTurno !== 'undefined') {
            break;
          }
        }

        if (typeof currentTurno === 'undefined' || typeof currentTurno.textComponents === 'undefined') {
          return {
            layout: 'standard',
            items: []
          };
        }

        if (typeof currentTurno.testoRiferimento.tipoPlaceholder !== 'undefined') {
          placeholderType = parseInt(currentTurno.testoRiferimento.tipoPlaceholder);
        }
        for (i = 0; i < currentTurno.textComponents.items.length; i ++) {
          selectedIdx = '-1';
          if (typeof progress[currentTurno.textComponents.items[i].questionId] !== 'undefined' && typeof progress[currentTurno.textComponents.items[i].questionId].answerId !== 'undefined') {
            selectedIdx = progress[currentTurno.textComponents.items[i].questionId].answerId;
          }
          switch (currentTurno.textComponents.items[i].type) {
            case 'html':
              continue;

            case 'dropdown_choices':
            case 'select':
              currentTurno.textComponents.items[i].options.forEach((elem, idx) => {elem.selected = false});
              currentTurno.textComponents.items[i].options[currentTurno.textComponents.items[i].optionsAK[selectedIdx]].selected = true;
              currentTurno.textComponents.items[i].currentViewing = currentTurno.textComponents.items[i].questionId == id;
              currentTurno.textComponents.items[i].answered = selectedIdx != '-1';
              currentTurno.textComponents.items[i].color = colore;
              currentTurno.textComponents.items[i].content = currentTurno.textComponents.items[i].options[currentTurno.textComponents.items[i].optionsAK[selectedIdx]].text;
              currentTurno.textComponents.items[i].options[0].visible = true;
              currentTurno.textComponents.items[i].options[0].text = this.translations['survey#select_answer'];
              if (currentTurno.textComponents.items[i].answered) {
                currentTurno.textComponents.items[i].options[0].text = this.translations['survey#deselect_answer'];
              }
              else if (currentTurno.textComponents.items[i].type === 'dropdown_choices') {
                currentTurno.textComponents.items[i].options[0].visible = false;
              }
              break;

            case 'placeholder':
              currentTurno.textComponents.items[i].currentViewing = currentTurno.textComponents.items[i].questionId == id;
              currentTurno.textComponents.items[i].answered = false;
              currentTurno.textComponents.items[i].color = colore;
              answerText = placeholderMaps[placeholderType].template.replace('X', currentTurno.textComponents.items[i].numericIdx);
              //answerTitle = '-1';
              currentTurno.textComponents.items[i].content = answerText;
              if (selectedIdx != '-1') {
                answerText = strip(currentTurno.textComponents.items[i].questionMeta['answer' + selectedIdx].text);
                //answerTitle = strip(currentTurno.textComponents.items[i].questionText).replace(':', '');
                currentTurno.textComponents.items[i].answered = true;
              }
              currentTurno.textComponents.items[i].content = answerText;
              break;
          }
        }

        return currentTurno.textComponents;
        /*
        turni.forEach((turno, turnoIndex) => {
          if(turno.associationIdMap){
            Object.keys(turno.associationIdMap).forEach(key => {
              associationIdMap[key] = turno.associationIdMap[key];
            });
          }
          if (typeof turno.layout !== 'undefined' && turno.layout === 'dropdown_choices') {
            selectedIdx = '-1';
            for (j = 0; j < turno.textComponents.length; j++) {
              if (turno.textComponents[j].type !== 'select') {
                continue;
              }
              if (typeof progress[turno.textComponents[j].questionId] !== 'undefined') {
                selectedIdx = progress[turno.textComponents[j].questionId];
              }
            }
            turno.textComponents[j].options[turno.textComponents[j].optionsAK[selectedIdx]].selected = true;
          }
        });



        let getTextAndTitleByQuestionAndAnswerId = (questionId, answerId) => {
          let result;
          turni.forEach(turno => {
            for (let j = 0; j < turno.domande.length; j++) {
              if (turno.domande[j].id === questionId) {
                for (let q in turno.domande[j].meta) {
                  if (turno.domande[j].meta.hasOwnProperty(q) &&
                    turno.domande[j].meta[q].id === answerId) {
                    result = {
                      text: strip(turno.domande[j].meta[q].text),
                      title: strip(turno.domande[j].text).replace(':', '')
                    };
                    break;
                  }
                }
              }
            }
          });
          return result;
        };

        if (typeof currentTurno !== 'undefined' && typeof currentTurno.layout !== 'undefined' && currentTurno.layout === 'dropdown_choices') {
          text = currentTurno.testoRiferimento.text;
          for (let j = 0; j < currentTurno.domande.length; j++) {
            text = text.replace(
              '____' + associationIdMap[currentTurno.domande[j].id] + '._____',
              selectAssociationIdMap[currentTurno.domande[j].id]
            );
          }

          return text;
        }

        for (q = 0; q < questionsData.length; q++) {
          if (questionsData[q].id === id &&
            questionsData[q].understandingText) {
            text = questionsData[q].understandingText;
            break;
          }
        }

        if (typeof text === 'undefined') {
          return;
        }

        for (i in progress) {
          if (progress.hasOwnProperty(i) &&
            associationIdMap[i] &&
            typeof progress[i].answerId !== 'undefined' &&
            progress[i].answerId !== -1) {
            currentAnswer = getTextAndTitleByQuestionAndAnswerId(i, progress[i].answerId);
            currentStyle = i === id ? baseStyle + 'font-weight: bold;' : baseStyle;

            text = text.replace(
              '____' + associationIdMap[i] + '._____',
              (typeof selectAssociationIdMap[i] === 'undefined' ? '' : selectAssociationIdMap[i]) +
              '<span class="answer"' +
              'style="' + currentStyle + '"' +
              'title="' + currentAnswer.title + '">' +
              currentAnswer.text +
              '</span>'
            );
          }
        }

        return text;
        */
      })
      ;//.distinctUntilChanged();


      this.newSectionError$ = this.assessmentService.getNewSectionError$();

      // on section error close modal
      this.newSectionError$
        .filter(hasError => hasError)
        .subscribe(() => this.store.dispatch(this.viewActions.closeModal()));

      // identify by fail
      this.socketHandler.message$
        .filter(message => message.type.indexOf('identify_by.response.fail') !== -1)
        .take(1)
        .subscribe(message => {
          // prima chiudere l'eventuale modale aperta
          this.store.dispatch(this.viewActions.closeModal());

          this.identifyByFailed = true;
        });
  }

  onSelectChangeAnswer ($event, turno) {
    console.log($event);
  }

  ngOnInit () {
    this.isLastSection$
      .takeUntil(this.destroyed$)
      .subscribe(isLast => this.isLastSection = isLast);

    this.currentQuestionTime$
      .takeUntil(this.destroyed$)
      .takeUntil(this.completed$.filter(complete => complete))
      .subscribe(({question, currentTime}) => {
        this.assessmentService.updateQuestionViewTime(
          question.id,
          currentTime
        );
      });

    this.completed$
      .takeUntil(this.destroyed$)
      .subscribe(completed => this.isCompleted = completed);

    this.countMap$
      .switchMap(countMap => this.currentQuestion$
        .map(({id}) => countMap[id]))
      .takeUntil(this.destroyed$)
      .subscribe(title => this.questionTitle = title);

    this.viewService.isFullscreen$()
      .filter(() => this.requestFullscreen)
      .takeUntil(this.destroyed$)
      .subscribe(isFullscreen => {
        if (!isFullscreen) {
          this.viewChange.emit('wait');
        }
      });

    this.viewService.isFullscreen$()
      .filter(() => this.requestFullscreen)
      .takeUntil(this.completed$.filter(complete => complete))
      .takeUntil(this.destroyed$)
      .subscribe(isFullscreen => {
        if (!isFullscreen) {
          this.socketHandler.guiEventRequest('test#fullScreenExit');
        }
      });

    this.pageEventsHandler.isMobilePaused$()
      .takeUntil(this.destroyed$)
      .subscribe(paused => {
        if(paused){
          this.store.dispatch(this.assessmentActions.lockTest());
          this.viewChange.emit('wait');
        }
      });

    /*
    Observable.timer(SYNC_FREQUENCY_SECS * 1000, SYNC_FREQUENCY_SECS * 1000)
      .takeUntil(this.completed$.filter(complete => complete))
      .takeUntil(this.destroyed$)
      .withLatestFrom(this.assessmentService.getCurrentSectionMetadata$())
      .subscribe(([, currentSectionMetadata]) => {
        let currentSectionViewTime = this.assessmentService.getSectionViewTime(currentSectionMetadata.id);
        if (parseInt(currentSectionMetadata.tempoEsecuzione) - parseInt(currentSectionViewTime) <= 1) {
          return;
        }
        this.store.dispatch(this.assessmentActions.forceViewTimeSync());
      });
      */

    this.translations$
      .takeUntil(this.destroyed$)
      .subscribe(translations => this.translations = translations);

    this.socketHandler.disconnect$
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        if (NOTIFY_CONNECTION_STATUS_WITH_GROWL) {
          this.store.dispatch(this.viewActions.addAlert({
            body: this.translations['survey#no_connection'],
            type: 'danger',
            preventAutoclose: true
          }));
        }
      });
    this.socketHandler.connect$
      .takeUntil(this.destroyed$)
      .subscribe(() => {
        if (NOTIFY_CONNECTION_STATUS_WITH_GROWL) {
          this.store.dispatch(this.viewActions.resetAlerts());
          Observable.timer(150)
            .take(1)
            .subscribe(() => {
              this.store.dispatch(this.viewActions.addAlert({
                body: this.translations['survey#connection_restored'],
                type: 'info'
              }));
            });
        }
      });

    this.socketHandler.connect$
      .takeUntil(this.destroyed$)
      .withLatestFrom(this.lastSectionTimeUpdateStatus$)
      .withLatestFrom(this.lastSectionTimeUpdateStatus$)
      .withLatestFrom(this.assessmentService.getCurrentSectionMetadata$())
      .subscribe(([[[, sectionTimeUpdateStatus], setAnswerStatus], currentSectionMetadata]) => {
        let delay = 3000
          , currentSectionViewTime = this.assessmentService.getSectionViewTime(currentSectionMetadata.id)
        ;

        if (parseInt(currentSectionMetadata.tempoEsecuzione) - parseInt(currentSectionViewTime) <= 3) {
          return;
        }

        Observable.timer(delay)
          .take(1)
          .subscribe(() => {
            currentSectionViewTime = this.assessmentService.getSectionViewTime(currentSectionMetadata.id);
            if (parseInt(currentSectionMetadata.tempoEsecuzione) - parseInt(currentSectionViewTime) <= 1) {
              return;
            }
            this.store.dispatch(this.assessmentActions.resendProgressAfterReconnect());
          });
        delay = 50000;
        /*
        if (sectionTimeUpdateStatus === false) {
          currentSectionViewTime = this.assessmentService.getSectionViewTime(currentSectionMetadata.id);
          if (parseInt(currentSectionMetadata.tempoEsecuzione) - parseInt(currentSectionViewTime) <= 4) {
            return;
          }
          Observable.timer(delay)
            .take(1)
            .subscribe(() => {
              currentSectionViewTime = this.assessmentService.getSectionViewTime(currentSectionMetadata.id);
              if (parseInt(currentSectionMetadata.tempoEsecuzione) - parseInt(currentSectionViewTime) <= 1) {
                return;
              }
              this.store.dispatch(this.assessmentActions.forceViewTimeSync());
            });
        }
        */
      });
  }

  onSendLog (questionId) {
    // update refresh enabled
    this.store.dispatch(this.assessmentActions.setQuestionRefreshEnabled({
      refresh_enabled: false,
      questionId: questionId
    }));

    // open modal
    this.store.dispatch(this.viewActions.openModal({
      title: this.translations['modal#refresh_title'],
      description: this.translations['modal#refresh_description'],
    }));

    // sendLog to mediator
    this.store.dispatch(this.assessmentActions.sendLog());
  }

  onPlay (event) {
    this.store.dispatch(this.viewActions.playAudio(event.audio));
    this.currentPlayerId$.next(event.id);
  }

  onStop () {
    this.store.dispatch(this.viewActions.stopAudio());
    this.currentPlayerId$.next(null);
  }

  onTerminate (showResult) {
    if (showResult) {
      this.viewChange.emit('report');
      return;
    }

    if (!this.isLastSection) {
      this.socketHandler.guiEventRequest('test#prematureTerminationRequest');
    }

    this.store.dispatch(this.viewActions.openModal({
      title: this.translations['modal#terminate_request_user_title'],
      submitText: this.translations['modal#terminate_request_user_submit'],
      checks: [
        this.translations['modal#terminate_request_user_check_1'],
        this.translations['modal#terminate_request_user_check_2']
      ],
      useCodeValidation: !this.isLastSection && !this.trainingMode,
      isWarning: true,
      action: this.assessmentActions.requestTerminateTest()
    }));
  }

  onRequestNewQuestion (question) {
    this.store.dispatch(this.assessmentActions.setCurrentQuestionId(question.id));
  }

  onRequestNewSection (section) {
    if (this.isCompleted) {
      this.store.dispatch(this.assessmentActions.setCurrentSectionId(section.id));
      return;
    }

    let sectionLabel = section.nome;

    this.store.dispatch(this.viewActions.openModal({
      modalId: 'new_section',
      title: `${this.translations['modal#change_section_title']} <strong>${sectionLabel}</strong>?`,
      submitText: this.translations['modal#change_section_submit'],
      description: this.freeInsert
        ? `${this.translations['modal#change_section_freeInsert']}`
        : `
          ${this.translations['modal#change_section_description_1']} 
          <strong>${sectionLabel}</strong> 
          ${this.translations['modal#change_section_description_2']} 
          ${this.translations['modal#change_section_description_3']}
        `,
      checks: this.freeInsert
        ? undefined
        : [
          `${this.translations['modal#change_section_check_1']} ${sectionLabel}`,
          this.translations['modal#change_section_check_2']
        ],
      isWarning: !this.freeInsert,
      action: this.assessmentActions.requestNewSectionBefore(section.id)
    }));

    // force sync
    this.doSync();
  }

  doSync(){
    this.assessmentService.getCurrentSectionId$()
      .withLatestFrom(this.assessmentService.getRicetteMetadata$())
      .take(1)
      .subscribe(([currentSectionId, ricettaMetadata]) => {
        this.assessmentService.viewTimeSyncHandler(currentSectionId, ricettaMetadata, false);
      });
  }

  onSetAnswer ({answer, question, ricetta}) {
    let timeLeft = this.shared['timerData'].timeLeft.split(':');
    timeLeft = parseInt(timeLeft[0]) * 60 + parseInt(timeLeft[1]);
    //if (!this.freeInsert && this.shared['timerData'].timePercentage > 98) {
    if (!this.freeInsert && timeLeft <= 0) {
      this.socketHandler.guiEventRequest('test#answerAfterTerminatedTime', {
        ricettaId: ricetta.id,
        questionId: question.id,
        answerId: answer.id
      });
      return;
    }

    this.store.dispatch(this.assessmentActions.setAnswer({
      ricettaId: ricetta.id,
      questionId: question.id,
      answerId: answer.id
    }));
  }

  refreshPageClick() {
    if(<any>WELCOME_PAGE === 'index.php'){
      // fix tablet
      let welcomePage = WELCOME_PAGE;
      if(this.utils.isMobile() || this.utils.isDesktop()){
        welcomePage = 'welcome_local.html';
      }
      document.location.href = welcomePage;
    } else {
      document.location.reload(true);
    }
  }

  increaseZoomHandler(){
    if(this.utils.isMobile()){
      this.increaseContentZoom();
    } else {
      this.increaseZoom.emit();
    }
  }

  decreaseZoomHandler(){
    if(this.utils.isMobile()){
      this.decreaseContentZoom();
    } else {
      this.decreaseZoom.emit();
    }
  }

  increaseContentZoom() {
    const [limit, step] = [140, 10];
    if (this.zoom < limit) {
      this.zoom += step;
    }
  }

  decreaseContentZoom() {
    const [limit, step] = [60, 10];
    if (this.zoom > limit) {
      this.zoom -= step;
    }
  }

  ngOnDestroy () {
    this.destroyed$.next();
  }
}



// WEBPACK FOOTER //
// ./src/app/layout/survey/survey.component.ts